const FENCED_CODE_BLOCK_OPEN_PATTERN = /^```([\w-]+)?\s*$/;
const FENCED_CODE_BLOCK_CLOSE_PATTERN = /^```\s*$/;
const HEADING_PATTERN = /^(#{1,6})\s+(.+)$/;
const UNORDERED_LIST_PATTERN = /^[-*+]\s+(.+)$/;
const ORDERED_LIST_PATTERN = /^(\d+)\.\s+(.+)$/;
// Capture groups:
// 1/2 inline code
// 3/4/5 markdown link
// 6/7 **strong**
// 8/9 __strong__
// 10/11 *emphasis*
// 12/13 _emphasis_
const INLINE_MARKDOWN_PATTERN =
  /(`([^`]*)`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*(.+?)\*\*)|(__(.+?)__)|(\*(.+?)\*)|(_(.+?)_)/g;

/**
 * Render small, intentionally limited Markdown subset.
 * Supports headings, paragraphs, inline emphasis, links, lists, and fenced code blocks.
 * Does not support nested lists, list continuation lines, full CommonMark emphasis rules,
 * or parenthesized URLs inside markdown links.
 */
export function renderMarkdown(markdown: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (trimmedLine.length === 0) {
      index += 1;
      continue;
    }

    if (FENCED_CODE_BLOCK_OPEN_PATTERN.test(trimmedLine)) {
      const { element, nextIndex } = renderCodeBlock(lines, index);
      fragment.appendChild(element);
      index = nextIndex;
      continue;
    }

    const headingMatch = trimmedLine.match(HEADING_PATTERN);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 6);
      const tagName = `h${level}` as keyof HTMLElementTagNameMap;
      const heading = document.createElement(tagName);
      appendInlineMarkdown(heading, headingMatch[2].trim());
      fragment.appendChild(heading);
      index += 1;
      continue;
    }

    if (UNORDERED_LIST_PATTERN.test(trimmedLine) || ORDERED_LIST_PATTERN.test(trimmedLine)) {
      const { element, nextIndex } = renderList(lines, index);
      fragment.appendChild(element);
      index = nextIndex;
      continue;
    }

    const { element, nextIndex } = renderParagraph(lines, index);
    fragment.appendChild(element);
    index = nextIndex;
  }

  return fragment;
}

function renderCodeBlock(
  lines: string[],
  startIndex: number,
): { element: HTMLPreElement; nextIndex: number } {
  const openingLine = lines[startIndex]?.trim() ?? '';
  const language = openingLine.match(FENCED_CODE_BLOCK_OPEN_PATTERN)?.[1];
  const code = document.createElement('code');
  const pre = document.createElement('pre');
  const codeLines: string[] = [];
  let index = startIndex + 1;

  while (index < lines.length && !FENCED_CODE_BLOCK_CLOSE_PATTERN.test(lines[index].trim())) {
    codeLines.push(lines[index]);
    index += 1;
  }

  if (language) {
    code.setAttribute('data-language', language);
  }

  code.textContent = codeLines.join('\n');
  pre.appendChild(code);

  return {
    element: pre,
    nextIndex: index < lines.length ? index + 1 : index,
  };
}

function renderList(
  lines: string[],
  startIndex: number,
): { element: HTMLUListElement | HTMLOListElement; nextIndex: number } {
  const firstLine = lines[startIndex].trim();
  const ordered = ORDERED_LIST_PATTERN.test(firstLine);
  const list = document.createElement(ordered ? 'ol' : 'ul');

  if (ordered) {
    const start = Number(firstLine.match(ORDERED_LIST_PATTERN)?.[1] ?? '1');
    list.setAttribute('start', String(start));
  }

  let index = startIndex;

  while (index < lines.length) {
    const trimmedLine = lines[index].trim();
    if (trimmedLine.length === 0) {
      break;
    }

    const match = ordered
      ? trimmedLine.match(ORDERED_LIST_PATTERN)
      : trimmedLine.match(UNORDERED_LIST_PATTERN);

    if (!match) {
      break;
    }

    const item = document.createElement('li');
    const itemContent = ordered ? match[2] : match[1];
    appendInlineMarkdown(item, itemContent.trim());
    list.appendChild(item);
    index += 1;
  }

  return { element: list, nextIndex: index };
}

function renderParagraph(
  lines: string[],
  startIndex: number,
): { element: HTMLParagraphElement; nextIndex: number } {
  const paragraph = document.createElement('p');
  let index = startIndex;
  let isFirstLine = true;

  while (index < lines.length) {
    const rawLine = lines[index];
    const trimmedLine = rawLine.trim();

    if (isMarkdownBlockBoundary(trimmedLine)) {
      break;
    }

    if (!isFirstLine) {
      paragraph.appendChild(document.createElement('br'));
    }

    appendInlineMarkdown(paragraph, trimmedLine);
    isFirstLine = false;
    index += 1;
  }

  return { element: paragraph, nextIndex: index };
}

function appendInlineMarkdown(container: HTMLElement, text: string, allowLinks = true): void {
  let cursor = 0;

  const inlineMarkdownPattern = createInlineMarkdownPattern();
  let match: RegExpExecArray | null = inlineMarkdownPattern.exec(text);

  while (match) {
    const matchIndex = match.index;
    const [fullMatch] = match;

    if (matchIndex > cursor) {
      container.appendChild(document.createTextNode(text.slice(cursor, matchIndex)));
    }

    if (match[1]) {
      const code = document.createElement('code');
      code.textContent = match[2];
      container.appendChild(code);
    } else if (match[3]) {
      const href = match[5];
      const safeUrl = allowLinks ? getSafeUrl(href) : null;

      if (safeUrl) {
        const link = document.createElement('a');
        link.href = safeUrl.href;
        link.target = '_blank';
        link.rel = 'noreferrer noopener';
        appendInlineMarkdown(link, match[4], false);
        container.appendChild(link);
      } else {
        container.appendChild(document.createTextNode(fullMatch));
      }
    } else if (match[6] || match[8]) {
      const strong = document.createElement('strong');
      appendInlineMarkdown(strong, match[7] ?? match[9] ?? '', allowLinks);
      container.appendChild(strong);
    } else if (match[10] || match[12]) {
      const emphasis = document.createElement('em');
      appendInlineMarkdown(emphasis, match[11] ?? match[13] ?? '', allowLinks);
      container.appendChild(emphasis);
    }

    cursor = matchIndex + fullMatch.length;

    if (fullMatch.length === 0) {
      break;
    }

    match = inlineMarkdownPattern.exec(text);
  }

  if (cursor < text.length) {
    container.appendChild(document.createTextNode(text.slice(cursor)));
  }
}

function createInlineMarkdownPattern(): RegExp {
  return new RegExp(INLINE_MARKDOWN_PATTERN.source, 'g');
}

function isMarkdownBlockBoundary(trimmedLine: string): boolean {
  return (
    trimmedLine.length === 0
    || FENCED_CODE_BLOCK_OPEN_PATTERN.test(trimmedLine)
    || HEADING_PATTERN.test(trimmedLine)
    || UNORDERED_LIST_PATTERN.test(trimmedLine)
    || ORDERED_LIST_PATTERN.test(trimmedLine)
  );
}

function getSafeUrl(href: string): URL | null {
  try {
    const url = new URL(href, window.location.origin);
    return ['http:', 'https:', 'mailto:'].includes(url.protocol) ? url : null;
  } catch {
    return null;
  }
}